import { FormEvent, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { createHttpClient, type HttpClient } from "@shared/api/httpClient";
import { formatLocalDateTime } from "@shared/utils/dateTime";
import {
  friendlyLabel,
  friendlyOptionLabel,
} from "@shared/utils/friendlyLabels";
import { displayNameForIdentity } from "@shared/utils/privacy";
import { statusPillClassName } from "@/utils/statusPill";

type SearchMode = "exact" | "contains" | "beginsWith";

type PackageSearchState = {
  clientId: string;
  packageType: string;
  packageStage: string;
  status: string;
  cmmcLevel: string;
  jobId: string;
  search: string;
  searchMode: SearchMode;
  limit: number;
};

type PackageRecord = Record<string, unknown> & {
  clientId?: string;
  accountId?: string;
  clientName?: string;
  packageId?: string;
  packageName?: string;
  finalAuditorPackageName?: string;
  jobId?: string;
  packageType?: string;
  packageStage?: string;
  status?: string;
  displayStatus?: string;
  packageLifecycleStatus?: string;
  cmmcLevel?: string;
  cmmc_level?: string;
  createdAt?: string;
  updatedAt?: string;
  indexedAt?: string;
  generatedUtc?: string;
  passCount?: number | string;
  partialCount?: number | string;
  failCount?: number | string;
  notApplicableCount?: number | string;
  totalControls?: number | string;
  finalReadinessPassControls?: number | string;
  finalReadinessPartialControls?: number | string;
  finalReadinessFailControls?: number | string;
  finalReadinessNotApplicableControls?: number | string;
  finalReadinessTotalControls?: number | string;
  documentCount?: number | string;
  signedDocumentCount?: number | string;
  signedDocumentsUploaded?: number | string;
  validDocumentCount?: number | string;
  invalidDocumentCount?: number | string;
  pendingDocumentCount?: number | string;
  scoringEligibleDocumentCount?: number | string;
  clientActionRequiredCount?: number | string;
  actionRequiredCount?: number | string;
  humanProcessRequiredCount?: number | string;
  possibleNotApplicableCount?: number | string;
  finalAssessorReviewPendingCount?: number | string;
  relatedFinalPackageId?: string;
  relatedFinalPackageName?: string;
  relatedTechnicalPackageId?: string;
  relatedTechnicalPackageName?: string;
  sourceTechnicalPackageId?: string;
  technicalPackageIntakeS3Uri?: string;
  finalAuditorPackageS3Uri?: string;
  packageS3Uri?: string;
  s3Uri?: string;
  scopeSummary?: Record<string, unknown>;
  scopeTotalAssets?: number | string;
  scopeInScopeAssets?: number | string;
  scopeOutOfScopeAssets?: number | string;
  scopeUnknownScopeAssets?: number | string;
  scopeCuiAssets?: number | string;
  scopeSecurityProtectionAssets?: number | string;
  scopeClientEvidencePackageAssets?: number | string;
  scopeEvidenceEligibleAssets?: number | string;
  scopeMissingRequiredTagFindings?: number | string;
  scopeInvalidTagFindings?: number | string;
  scopeValidationStatus?: string;
  scopeUnknownScopeClassificationPresent?: boolean | string;
  scopeUnknownScopeLikelyAwsManaged?: number | string;
  scopeUnknownScopeLikelyControlTower?: number | string;
  scopeUnknownScopeLikelyCdkBootstrap?: number | string;
  scopeUnknownScopeLikelyPlatformSupport?: number | string;
  scopeUnknownScopeClientCreatedUnknown?: number | string;
  scopeUnknownScopeRequiresClientReview?: number | string;
  resourceScopeMetadataPresent?: boolean | string;
};

type PackagesResponse = {
  packages?: PackageRecord[];
  items?: PackageRecord[];
  count?: number;
  matchedCount?: number;
  page?: number;
  pageCount?: number;
  limit?: number;
  hasPreviousPage?: boolean;
  hasNextPage?: boolean;
};

type DownloadStatus = {
  packageKey?: string;
  state: "idle" | "preparing" | "error";
  message?: string;
};

type PackageDownloadUrlResponse = {
  downloadUrl: string;
  method?: "GET" | string;
  expiresInSeconds?: number;
  packageId?: string;
  packageName?: string;
  fileName?: string;
};

const DEFAULT_SEARCH: PackageSearchState = {
  clientId: "",
  packageType: "",
  packageStage: "",
  status: "",
  cmmcLevel: "",
  jobId: "",
  search: "",
  searchMode: "contains",
  limit: 5,
};

const PACKAGE_TYPE_OPTIONS = [
  "TECHNICAL_EVIDENCE_PACKAGE",
  "FINAL_AUDITOR_PACKAGE",
];
const PACKAGE_STAGE_OPTIONS = [
  "TECHNICAL_BASELINE",
  "FINAL_AUDITOR_READY",
  "CLIENT_ACTION_REQUIRED",
];
const PACKAGE_STATUS_OPTIONS = [
  "PACKAGE_INDEXED",
  "AUDITOR_PACKAGE_READY",
  "CLIENT_ACTION_REQUIRED",
  "FINAL_ASSESSOR_REVIEW_PENDING",
  "FAILED",
];
const CMMC_LEVEL_OPTIONS = ["L1", "L2", "L3"];
const PAGE_SIZE_OPTIONS = [5, 25, 50, 100, 250];

function text(record: PackageRecord | undefined, ...keys: string[]): string {
  const item = record ?? {};
  for (const key of keys) {
    const value = item[key];
    if (value !== undefined && value !== null && String(value).trim() !== "")
      return String(value);
  }
  return "";
}

function value(value: unknown): string {
  if (value === undefined || value === null || String(value).trim() === "")
    return "—";
  if (Array.isArray(value)) return value.join(", ") || "—";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function numberValue(record: PackageRecord, ...keys: string[]): number {
  for (const key of keys) {
    const raw = record[key];
    if (typeof raw === "number" && Number.isFinite(raw)) return raw;
    if (typeof raw === "string" && raw.trim()) {
      const parsed = Number(raw);
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  return 0;
}

function objectValue(
  record: PackageRecord | undefined,
  key: string,
): Record<string, unknown> {
  const value = (record ?? {})[key];
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function scopeNumber(
  pkg: PackageRecord,
  nestedKey: string,
  ...flatKeys: string[]
): number {
  const nested = objectValue(pkg, "scopeSummary");
  for (const key of flatKeys) {
    const value = numberValue(pkg, key);
    if (value) return value;
  }
  const nestedValue = nested[nestedKey];
  if (typeof nestedValue === "number" && Number.isFinite(nestedValue))
    return nestedValue;
  if (typeof nestedValue === "string" && nestedValue.trim()) {
    const parsed = Number(nestedValue);
    if (Number.isFinite(parsed)) return parsed;
  }
  return 0;
}

function scopeCounts(pkg: PackageRecord) {
  const nested = objectValue(pkg, "scopeSummary");
  return {
    total: scopeNumber(pkg, "totalAssets", "scopeTotalAssets"),
    inScope: scopeNumber(pkg, "inScopeAssets", "scopeInScopeAssets"),
    outOfScope: scopeNumber(pkg, "outOfScopeAssets", "scopeOutOfScopeAssets"),
    unknown: scopeNumber(pkg, "unknownScopeAssets", "scopeUnknownScopeAssets"),
    cui: scopeNumber(pkg, "cuiAssets", "scopeCuiAssets"),
    securityProtection: scopeNumber(
      pkg,
      "securityProtectionAssets",
      "scopeSecurityProtectionAssets",
    ),
    clientEvidencePackage: scopeNumber(
      pkg,
      "clientEvidencePackageAssets",
      "scopeClientEvidencePackageAssets",
    ),
    evidenceEligible: scopeNumber(
      pkg,
      "evidenceEligibleAssets",
      "scopeEvidenceEligibleAssets",
    ),
    missingTags: scopeNumber(
      pkg,
      "missingRequiredTagFindings",
      "scopeMissingRequiredTagFindings",
    ),
    invalidTags: scopeNumber(
      pkg,
      "invalidTagFindings",
      "scopeInvalidTagFindings",
    ),
    validationStatus:
      text(pkg, "scopeValidationStatus") ||
      String(nested.validationStatus || ""),
  };
}

function boolValue(pkg: PackageRecord, key: string): boolean {
  const raw = pkg[key];
  if (typeof raw === "boolean") return raw;
  if (typeof raw === "string") return raw.trim().toLowerCase() === "true";
  return false;
}

function scopeReviewCounts(pkg: PackageRecord) {
  const nested = objectValue(pkg, "scopeSummary");
  const awsManaged = scopeNumber(
    pkg,
    "unknownScopeLikelyAwsManaged",
    "scopeUnknownScopeLikelyAwsManaged",
  );
  const controlTower = scopeNumber(
    pkg,
    "unknownScopeLikelyControlTower",
    "scopeUnknownScopeLikelyControlTower",
  );
  const cdkBootstrap = scopeNumber(
    pkg,
    "unknownScopeLikelyCdkBootstrap",
    "scopeUnknownScopeLikelyCdkBootstrap",
  );
  const platformSupport = scopeNumber(
    pkg,
    "unknownScopeLikelyPlatformSupport",
    "scopeUnknownScopeLikelyPlatformSupport",
  );
  const clientCreated = scopeNumber(
    pkg,
    "unknownScopeClientCreatedUnknown",
    "scopeUnknownScopeClientCreatedUnknown",
  );
  const requiresReview = scopeNumber(
    pkg,
    "unknownScopeRequiresClientReview",
    "scopeUnknownScopeRequiresClientReview",
  );
  const unknown = scopeNumber(
    pkg,
    "unknownScopeAssets",
    "scopeUnknownScopeAssets",
  );
  const classificationPresent =
    boolValue(pkg, "scopeUnknownScopeClassificationPresent") ||
    String(nested.unknownScopeClassificationPresent || "").toLowerCase() ===
      "true";
  const likelyManagedOrPlatform =
    awsManaged + controlTower + cdkBootstrap + platformSupport;
  return {
    classificationPresent,
    unknown,
    awsManaged,
    controlTower,
    cdkBootstrap,
    platformSupport,
    clientCreated,
    requiresReview,
    likelyManagedOrPlatform,
  };
}

function hasScopeReviewContext(pkg: PackageRecord): boolean {
  const counts = scopeReviewCounts(pkg);
  return Boolean(
    counts.classificationPresent ||
    counts.awsManaged ||
    counts.controlTower ||
    counts.cdkBootstrap ||
    counts.platformSupport ||
    counts.clientCreated ||
    counts.requiresReview,
  );
}

function scopeReviewCompactSummary(pkg: PackageRecord): string {
  if (!hasScopeReviewContext(pkg)) return "";
  const counts = scopeReviewCounts(pkg);
  return `Scope review context: ${counts.likelyManagedOrPlatform} likely AWS/platform generated / ${counts.clientCreated} client-created unknown / ${counts.requiresReview} requires review`;
}

function hasScopeMetadata(pkg: PackageRecord): boolean {
  const counts = scopeCounts(pkg);
  return Boolean(
    text(pkg, "resourceScopeMetadataPresent").toLowerCase() === "true" ||
    text(pkg, "scopeValidationStatus") ||
    Object.keys(objectValue(pkg, "scopeSummary")).length > 0 ||
    counts.total ||
    counts.inScope ||
    counts.outOfScope ||
    counts.unknown ||
    counts.missingTags ||
    counts.invalidTags,
  );
}

function scopeSummary(pkg: PackageRecord): string {
  if (!hasScopeMetadata(pkg)) return "—";
  const counts = scopeCounts(pkg);
  const parts = [
    `In ${counts.inScope}`,
    `Out ${counts.outOfScope}`,
    `Unknown ${counts.unknown}`,
  ];
  if (counts.total) parts.push(`Total ${counts.total}`);
  return parts.join(" / ");
}

function scopeExceptionSummary(pkg: PackageRecord): string {
  if (!hasScopeMetadata(pkg)) return "—";
  const counts = scopeCounts(pkg);
  const parts = [];
  if (counts.unknown) parts.push(`${counts.unknown} unknown scope`);
  if (counts.missingTags)
    parts.push(
      `${counts.missingTags} missing tag finding${counts.missingTags === 1 ? "" : "s"}`,
    );
  if (counts.invalidTags)
    parts.push(
      `${counts.invalidTags} invalid tag finding${counts.invalidTags === 1 ? "" : "s"}`,
    );
  if (counts.validationStatus)
    parts.push(`Validation ${counts.validationStatus}`);
  return parts.length ? parts.join(" / ") : "No scope exceptions indexed";
}

function scopeAssetSummary(pkg: PackageRecord): string {
  if (!hasScopeMetadata(pkg)) return "—";
  const counts = scopeCounts(pkg);
  const parts = [];
  if (counts.cui) parts.push(`${counts.cui} CUI`);
  if (counts.securityProtection)
    parts.push(`${counts.securityProtection} security protection`);
  if (counts.clientEvidencePackage)
    parts.push(`${counts.clientEvidencePackage} evidence package`);
  if (counts.evidenceEligible)
    parts.push(`${counts.evidenceEligible} evidence eligible`);
  return parts.length ? parts.join(" / ") : "—";
}

function packageId(pkg: PackageRecord): string {
  return text(pkg, "logicalPackageId", "packageId", "id", "packageName", "finalAuditorPackageId");
}

function packageName(pkg: PackageRecord): string {
  return (
    text(pkg, "packageName", "finalAuditorPackageName", "packageId") ||
    "Evidence package"
  );
}

function compactPackageIdentifier(identifier: string, maxLength = 42): string {
  const value = String(identifier || "").trim();
  if (!value) return "—";
  if (value.length <= maxLength) return value;
  return `${value.slice(0, Math.max(10, maxLength - 14))}…${value.slice(-10)}`;
}

function packageGeneratedLabel(pkg: PackageRecord): string {
  const candidate =
    text(pkg, "generatedUtc", "createdAt", "indexedAt") || packageId(pkg);
  const match = candidate.match(/20\d{2}-\d{2}-\d{2}T\d{2}[-:]\d{2}[-:]\d{2}Z/);
  if (!match) return "";
  const isoTimestamp = match[0].replace(
    /T(\d{2})-(\d{2})-(\d{2})Z$/,
    "T$1:$2:$3Z",
  );
  return formatLocalDateTime(isoTimestamp);
}

function packageDisplayTitle(pkg: PackageRecord): string {
  const id = packageId(pkg);
  const explicitName = text(pkg, "finalAuditorPackageName", "packageName");
  if (explicitName && explicitName !== id) return explicitName;
  const type = packageTypeLabel(pkg) || "Evidence package";
  const generated = packageGeneratedLabel(pkg);
  return generated && generated !== "—" ? `${type} · ${generated}` : type;
}

function packageOwnerFields(pkg: PackageRecord): Array<[string, unknown]> {
  const rows: Array<[string, unknown]> = [
    ["Company", text(pkg, "clientName", "companyName")],
    ["Client ID", text(pkg, "clientId")],
    [
      "Account ID",
      text(
        pkg,
        "accountId",
        "customerAccountId",
        "workloadAccountId",
        "awsAccountId",
      ),
    ],
    ["Region", text(pkg, "region", "awsRegion")],
    [
      "Owner",
      text(
        pkg,
        "owner",
        "ownerName",
        "packageOwner",
        "customerOwner",
        "engagementOwner",
      ),
    ],
    [
      "Contact",
      displayNameForIdentity(
        {
          name: text(pkg, "ownerName", "customerOwner", "engagementOwner"),
          email: text(
            pkg,
            "ownerEmail",
            "customerContactEmail",
            "invitationEmail",
            "primaryContactEmail",
          ),
        },
        "Package contact",
      ),
    ],
  ];
  return rows.filter(([, detailValue]) => value(detailValue) !== "—");
}

function packageKey(pkg: PackageRecord, index: number): string {
  return `${text(pkg, "clientId") || "client"}#${packageId(pkg) || index}`;
}

function rawPackageStatus(pkg: PackageRecord): string {
  return (
    text(
      pkg,
      "displayStatus",
      "status",
      "packageLifecycleStatus",
      "jobStatus",
    ) || "UNKNOWN"
  );
}

function packageStatus(pkg: PackageRecord): string {
  return friendlyLabel(rawPackageStatus(pkg));
}

function rawPackageType(pkg: PackageRecord): string {
  return text(pkg, "packageType", "type");
}

function packageTypeLabel(pkg: PackageRecord): string {
  return friendlyLabel(rawPackageType(pkg));
}

function rawPackageStage(pkg: PackageRecord): string {
  return text(pkg, "packageStage", "stage");
}

function packageStageLabel(pkg: PackageRecord): string {
  return friendlyLabel(rawPackageStage(pkg));
}

function packageUpdatedAt(pkg: PackageRecord): string {
  return text(pkg, "updatedAt", "indexedAt", "createdAt", "generatedUtc");
}

function readiness(pkg: PackageRecord) {
  const pass = numberValue(
    pkg,
    "passCount",
    "pass",
    "finalReadinessPassControls",
    "PASS",
  );
  const partial = numberValue(
    pkg,
    "partialCount",
    "partial",
    "finalReadinessPartialControls",
    "PARTIAL",
  );
  const fail = numberValue(
    pkg,
    "failCount",
    "failedCount",
    "fail",
    "finalReadinessFailControls",
    "FAIL",
  );
  const notApplicable = numberValue(
    pkg,
    "notApplicableCount",
    "notApplicable",
    "finalReadinessNotApplicableControls",
    "NOT_APPLICABLE",
  );
  const total =
    numberValue(pkg, "totalControls", "total", "finalReadinessTotalControls") ||
    pass + partial + fail + notApplicable;
  return { pass, partial, fail, notApplicable, total };
}

function readinessSummary(pkg: PackageRecord): string {
  const counts = readiness(pkg);
  if (
    !counts.total &&
    !counts.pass &&
    !counts.partial &&
    !counts.fail &&
    !counts.notApplicable
  )
    return "—";
  return `PASS ${counts.pass} / PARTIAL ${counts.partial} / FAIL ${counts.fail} / N/A ${counts.notApplicable} / TOTAL ${counts.total}`;
}

function readinessPercent(pkg: PackageRecord): number | null {
  const counts = readiness(pkg);
  if (!counts.total) return null;
  return (
    Math.round(((counts.pass + counts.notApplicable) / counts.total) * 1000) /
    10
  );
}

function ReadinessBadges({
  pkg,
  compact = false,
}: {
  pkg: PackageRecord;
  compact?: boolean;
}) {
  const counts = readiness(pkg);
  if (
    !counts.total &&
    !counts.pass &&
    !counts.partial &&
    !counts.fail &&
    !counts.notApplicable
  )
    return <span>—</span>;
  const resolvedPercent = readinessPercent(pkg);
  return (
    <div
      className={
        compact
          ? "readiness-chip-grid readiness-chip-grid-compact"
          : "readiness-chip-grid"
      }
    >
      <span className="readiness-chip readiness-chip-pass">
        <strong>{counts.pass}</strong> PASS
      </span>
      <span className="readiness-chip readiness-chip-partial">
        <strong>{counts.partial}</strong> PARTIAL
      </span>
      <span className="readiness-chip readiness-chip-fail">
        <strong>{counts.fail}</strong> FAIL
      </span>
      <span className="readiness-chip readiness-chip-na">
        <strong>{counts.notApplicable}</strong> N/A
      </span>
      <span className="readiness-chip readiness-chip-total">
        <strong>{counts.total}</strong> TOTAL
      </span>
      {!compact && resolvedPercent !== null && (
        <span className="readiness-chip readiness-chip-score">
          <strong>{resolvedPercent}%</strong> resolved
        </span>
      )}
    </div>
  );
}

function documentSummary(pkg: PackageRecord): string {
  const total = numberValue(pkg, "documentCount");
  const signed = numberValue(
    pkg,
    "signedDocumentCount",
    "signedDocumentsUploaded",
  );
  const valid = numberValue(pkg, "validDocumentCount");
  const pending = numberValue(pkg, "pendingDocumentCount");
  const scoring = numberValue(pkg, "scoringEligibleDocumentCount");
  const parts = [];
  if (total) parts.push(`${total} docs`);
  if (signed) parts.push(`${signed} signed`);
  if (valid) parts.push(`${valid} valid`);
  if (pending) parts.push(`${pending} pending`);
  if (scoring) parts.push(`${scoring} scoring`);
  return parts.length ? parts.join(" / ") : "—";
}

function actionSummary(pkg: PackageRecord): string {
  const actionRequired = numberValue(
    pkg,
    "clientActionRequiredCount",
    "actionRequiredCount",
  );
  const human = numberValue(pkg, "humanProcessRequiredCount");
  const possibleNa = numberValue(pkg, "possibleNotApplicableCount");
  const assessor = numberValue(pkg, "finalAssessorReviewPendingCount");
  const parts = [];
  if (actionRequired)
    parts.push(`${actionRequired} action${actionRequired === 1 ? "" : "s"}`);
  if (human) parts.push(`${human} human/process`);
  if (possibleNa) parts.push(`${possibleNa} possible N/A`);
  if (assessor) parts.push(`${assessor} assessor review`);
  return parts.length ? parts.join(" / ") : "—";
}

function relatedPackage(pkg: PackageRecord): string {
  const finalId = text(pkg, "relatedFinalPackageName", "relatedFinalPackageId");
  if (finalId) return `Final: ${finalId}`;
  const technicalId = text(
    pkg,
    "relatedTechnicalPackageName",
    "relatedTechnicalPackageId",
    "sourceTechnicalPackageId",
  );
  if (technicalId) return `Technical: ${technicalId}`;
  return "—";
}

function locationSummary(pkg: PackageRecord): string {
  return (
    text(
      pkg,
      "technicalPackageIntakeS3Uri",
      "finalAuditorPackageS3Uri",
      "packageS3Uri",
      "s3Uri",
      "s3Key",
      "finalAuditorPackageKey",
    ) || "—"
  );
}

function detailRow(label: string, detailValue: unknown) {
  return (
    <div className="detail-row" key={label}>
      <dt>{label}</dt>
      <dd>{value(detailValue)}</dd>
    </div>
  );
}

async function requestPackageDownloadUrl(
  api: HttpClient,
  pkg: PackageRecord,
): Promise<PackageDownloadUrlResponse> {
  const clientId = text(pkg, "clientId");
  const accountId = text(pkg, "accountId");
  const id = packageId(pkg);
  if (!clientId || !accountId || !id)
    throw new Error(
      "Client ID, account ID, and package ID are required to generate a download link.",
    );

  const response = await api.post<PackageDownloadUrlResponse>(
    `/admin/clients/${encodeURIComponent(clientId)}/packages/${encodeURIComponent(id)}/download-url`,
    {},
    { query: { accountId } },
  );
  if (!response.downloadUrl)
    throw new Error("The API did not return a download URL.");
  return response;
}

function openDownloadUrlOnce(downloadUrl: string): void {
  const link = document.createElement("a");
  link.href = downloadUrl;
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  link.remove();
}

function PackageDetails({ pkg }: { pkg: PackageRecord }) {
  const percent = readinessPercent(pkg);
  return (
    <article className="card detail-panel package-admin-detail-panel">
      <div className="detail-panel-header">
        <div>
          <h3>Package Details</h3>
          <p className="muted-text">
            {text(pkg, "clientName", "clientId") || "Client"} ·{" "}
            {packageDisplayTitle(pkg)}
          </p>
          {text(pkg, "clientId") && text(pkg, "accountId") && packageId(pkg) && (
            <p>
              <Link
                className="secondary-button"
                to={`/evidence-packages/${encodeURIComponent(text(pkg, "clientId"))}/${encodeURIComponent(text(pkg, "accountId"))}/${encodeURIComponent(packageId(pkg))}`}
              >
                Open control reason/action detail
              </Link>
            </p>
          )}
        </div>
        <span className={statusPillClassName(rawPackageStatus(pkg))}>
          {packageStatus(pkg)}
        </span>
      </div>

      <div className="detail-grid">
        <section>
          <h4>Identity</h4>
          <dl>
            {detailRow("Client ID", pkg.clientId)}
            {detailRow("Client Name", pkg.clientName)}
            {detailRow("Package ID", packageId(pkg))}
            {packageName(pkg) !== packageId(pkg) &&
              detailRow("Package Name", packageName(pkg))}
            {detailRow("Job ID", pkg.jobId)}
            {detailRow("CMMC Level", text(pkg, "cmmcLevel", "cmmc_level"))}
          </dl>
        </section>

        <section>
          <h4>Lifecycle</h4>
          <dl>
            <div className="detail-row">
              <dt>Type</dt>
              <dd>
                <span
                  className={statusPillClassName(
                    rawPackageType(pkg) || packageTypeLabel(pkg),
                  )}
                >
                  {packageTypeLabel(pkg)}
                </span>
              </dd>
            </div>
            <div className="detail-row">
              <dt>Stage</dt>
              <dd>
                <span
                  className={statusPillClassName(
                    rawPackageStage(pkg) || packageStageLabel(pkg),
                  )}
                >
                  {packageStageLabel(pkg)}
                </span>
              </dd>
            </div>
            <div className="detail-row">
              <dt>Status</dt>
              <dd>
                <span className={statusPillClassName(rawPackageStatus(pkg))}>
                  {packageStatus(pkg)}
                </span>
              </dd>
            </div>
            {detailRow("Created", formatLocalDateTime(pkg.createdAt))}
            {detailRow("Updated", formatLocalDateTime(packageUpdatedAt(pkg)))}
            {detailRow("Related Package", relatedPackage(pkg))}
          </dl>
        </section>

        <section>
          <h4>Readiness</h4>
          <dl>
            <div className="detail-row detail-row-full">
              <dt>Control results</dt>
              <dd>
                <ReadinessBadges pkg={pkg} />
              </dd>
            </div>
            {detailRow(
              "Resolved Percent",
              percent === null ? "—" : `${percent}%`,
            )}
          </dl>
        </section>

        <section>
          <h4>Documents & actions</h4>
          <dl>
            {detailRow("Documents", documentSummary(pkg))}
            {detailRow("Actions", actionSummary(pkg))}
            {detailRow(
              "Generated reports",
              text(
                pkg,
                "generatedReportDocuments",
                "generatedReportDocumentCount",
              ),
            )}
          </dl>
        </section>

        <section>
          <h4>Scope</h4>
          <dl>
            {detailRow("Scope assets", scopeSummary(pkg))}
            {detailRow("Scope categories", scopeAssetSummary(pkg))}
            {detailRow("Scope exceptions", scopeExceptionSummary(pkg))}
          </dl>
        </section>

        {hasScopeReviewContext(pkg) && (
          <section>
            <h4>Scope review context</h4>
            <dl>
              {detailRow(
                "Unknown-scope assets",
                scopeReviewCounts(pkg).unknown,
              )}
              {detailRow(
                "Likely AWS-managed/security-service",
                scopeReviewCounts(pkg).awsManaged,
              )}
              {detailRow(
                "Likely Control Tower baseline",
                scopeReviewCounts(pkg).controlTower,
              )}
              {detailRow(
                "Likely CDK bootstrap",
                scopeReviewCounts(pkg).cdkBootstrap,
              )}
              {detailRow(
                "Likely platform support",
                scopeReviewCounts(pkg).platformSupport,
              )}
              {detailRow(
                "Client-created unknown",
                scopeReviewCounts(pkg).clientCreated,
              )}
              {detailRow(
                "Requires client review",
                scopeReviewCounts(pkg).requiresReview,
              )}
            </dl>
          </section>
        )}

        {packageOwnerFields(pkg).length > 0 && (
          <section>
            <h4>Client / owner</h4>
            <dl>
              {packageOwnerFields(pkg).map(([label, detailValue]) =>
                detailRow(label, detailValue),
              )}
            </dl>
          </section>
        )}

        <section>
          <h4>Storage</h4>
          <dl>
            {detailRow("Download Location", locationSummary(pkg))}
            {detailRow(
              "Technical Intake Bucket",
              pkg.technicalPackageIntakeBucket,
            )}
            {detailRow("Technical Intake Key", pkg.technicalPackageIntakeKey)}
            {detailRow("Final Auditor Bucket", pkg.finalAuditorPackageBucket)}
            {detailRow("Final Auditor Key", pkg.finalAuditorPackageKey)}
          </dl>
        </section>
      </div>

      <details className="raw-details">
        <summary>Raw package metadata</summary>
        <pre>{JSON.stringify(pkg, null, 2)}</pre>
      </details>
    </article>
  );
}

export default function EvidencePackagesPage() {
  const api = useMemo(() => createHttpClient({ portal: "admin" }), []);
  const [packages, setPackages] = useState<PackageRecord[]>([]);
  const [search, setSearch] = useState<PackageSearchState>(DEFAULT_SEARCH);
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(0);
  const [matchedCount, setMatchedCount] = useState(0);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<PackageRecord | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadStatus, setDownloadStatus] = useState<DownloadStatus>({
    state: "idle",
  });

  async function loadPackages(
    criteria: PackageSearchState = search,
    requestedPage = page,
  ) {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get<PackagesResponse>("/admin/packages", {
        query: {
          clientId: criteria.clientId || undefined,
          packageType: criteria.packageType || undefined,
          packageStage: criteria.packageStage || undefined,
          status: criteria.status || undefined,
          cmmcLevel: criteria.cmmcLevel || undefined,
          jobId: criteria.jobId || undefined,
          search: criteria.search || undefined,
          searchMode: criteria.searchMode,
          limit: criteria.limit,
          page: requestedPage,
        },
      });
      const items = response.packages ?? response.items ?? [];
      setPackages(items);
      const nextMatchedCount =
        response.matchedCount ?? response.count ?? items.length;
      const nextPage = response.page ?? requestedPage;
      const nextPageCount =
        response.pageCount ??
        (nextMatchedCount > 0
          ? Math.ceil(nextMatchedCount / criteria.limit)
          : 0);
      setMatchedCount(nextMatchedCount);
      setPageCount(nextPageCount);
      setHasPreviousPage(response.hasPreviousPage ?? nextPage > 1);
      setHasNextPage(
        response.hasNextPage ?? (nextPageCount > 0 && nextPage < nextPageCount),
      );
      setPage(nextPage);
      setSelectedPackage((current) => {
        if (!current) return items[0] ?? null;
        const currentKey = `${text(current, "clientId")}#${packageId(current)}`;
        return (
          items.find(
            (item) =>
              `${text(item, "clientId")}#${packageId(item)}` === currentKey,
          ) ??
          items[0] ??
          null
        );
      });
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Unable to load evidence packages.",
      );
      setPackages([]);
      setMatchedCount(0);
      setPageCount(0);
      setHasPreviousPage(false);
      setHasNextPage(false);
      setSelectedPackage(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadPackages(DEFAULT_SEARCH, 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  function handleSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setPage(1);
    void loadPackages(search, 1);
  }

  function handleClear() {
    setSearch(DEFAULT_SEARCH);
    setPage(1);
    void loadPackages(DEFAULT_SEARCH, 1);
  }

  function goToPage(nextPage: number) {
    const safePage = Math.min(Math.max(1, nextPage), pageCount || nextPage);
    setPage(safePage);
    void loadPackages(search, safePage);
  }

  function PaginationControls() {
    if (!matchedCount) return null;
    return (
      <div className="pagination-row">
        <button
          className="secondary-button"
          type="button"
          disabled={!hasPreviousPage || loading}
          onClick={() => goToPage(page - 1)}
        >
          Previous
        </button>
        <span className="muted-text">
          Page {pageCount ? page : 0}
          {pageCount ? ` of ${pageCount}` : ""}
        </span>
        <button
          className="secondary-button"
          type="button"
          disabled={!hasNextPage || loading}
          onClick={() => goToPage(page + 1)}
        >
          Next
        </button>
      </div>
    );
  }

  async function handleDownload(pkg: PackageRecord) {
    const key = `${text(pkg, "clientId")}#${packageId(pkg)}`;
    setDownloadStatus({ packageKey: key, state: "preparing" });
    try {
      const response = await requestPackageDownloadUrl(api, pkg);
      openDownloadUrlOnce(response.downloadUrl);
      setDownloadStatus({ state: "idle" });
    } catch (err) {
      setDownloadStatus({
        packageKey: key,
        state: "error",
        message:
          err instanceof Error
            ? err.message
            : "Unable to prepare package download.",
      });
    }
  }

  const currentRange =
    packages.length > 0
      ? `${(page - 1) * search.limit + 1}-${(page - 1) * search.limit + packages.length}`
      : "0";

  return (
    <div className="page-stack package-admin-page">
      <div className="page-heading">
        <h2>Evidence Packages</h2>
        <p>
          Search package metadata across clients, review readiness signals, and
          generate controlled download links for technical and final auditor
          packages.
        </p>
      </div>

      <article className="card package-visibility-section">
        <div className="grid-header-row">
          <div>
            <h3>Package Search</h3>
          </div>
          <button
            className="secondary-button"
            type="button"
            onClick={() => void loadPackages(search, page)}
          >
            Refresh
          </button>
        </div>

        <form className="form-grid package-search-grid" onSubmit={handleSearch}>
          <label>
            Client ID or Name
            <input
              value={search.clientId}
              onChange={(event) =>
                setSearch({ ...search, clientId: event.target.value })
              }
              placeholder="surelinc"
            />
          </label>
          <label>
            Package Type
            <select
              value={search.packageType}
              onChange={(event) =>
                setSearch({ ...search, packageType: event.target.value })
              }
            >
              <option value="">All package types</option>
              {PACKAGE_TYPE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {friendlyOptionLabel(option, "All package types")}
                </option>
              ))}
            </select>
          </label>
          <label>
            Package Stage
            <select
              value={search.packageStage}
              onChange={(event) =>
                setSearch({ ...search, packageStage: event.target.value })
              }
            >
              <option value="">All stages</option>
              {PACKAGE_STAGE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {friendlyOptionLabel(option, "All stages")}
                </option>
              ))}
            </select>
          </label>
          <label>
            Status
            <select
              value={search.status}
              onChange={(event) =>
                setSearch({ ...search, status: event.target.value })
              }
            >
              <option value="">All statuses</option>
              {PACKAGE_STATUS_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {friendlyOptionLabel(option, "All statuses")}
                </option>
              ))}
            </select>
          </label>
          <label>
            CMMC Level
            <select
              value={search.cmmcLevel}
              onChange={(event) =>
                setSearch({ ...search, cmmcLevel: event.target.value })
              }
            >
              <option value="">All levels</option>
              {CMMC_LEVEL_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
          <label>
            Job ID
            <input
              value={search.jobId}
              onChange={(event) =>
                setSearch({ ...search, jobId: event.target.value })
              }
              placeholder="Workflow/job ID"
            />
          </label>
          <label className="form-wide">
            Search
            <input
              value={search.search}
              onChange={(event) =>
                setSearch({ ...search, search: event.target.value })
              }
              placeholder="Package ID, S3 key, status, related package, workflow ID"
            />
          </label>
          <label>
            Search Mode
            <select
              value={search.searchMode}
              onChange={(event) =>
                setSearch({
                  ...search,
                  searchMode: event.target.value as SearchMode,
                })
              }
            >
              <option value="contains">Contains</option>
              <option value="beginsWith">Begins with</option>
              <option value="exact">Exact token</option>
            </select>
          </label>
          <label>
            Limit
            <select
              value={search.limit}
              onChange={(event) =>
                setSearch({ ...search, limit: Number(event.target.value) })
              }
            >
              {PAGE_SIZE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
          <div className="form-actions button-row form-wide search-action-row">
            <button className="primary-button" type="submit">
              Search Packages
            </button>
            <button
              className="secondary-button"
              type="button"
              onClick={handleClear}
            >
              Clear
            </button>
          </div>
        </form>
      </article>

      <article className="card">
        <div className="grid-header-row">
          <div>
            <h3>Package Registry</h3>
            <p className="muted-text">
              Showing {currentRange} of {matchedCount} matching package
              record(s).
            </p>
          </div>
          <span className="result-count">
            Showing {packages.length} of {matchedCount} matched · Page{" "}
            {pageCount ? page : 0}
            {pageCount ? ` of ${pageCount}` : ""}
          </span>
        </div>

        {loading && <p>Loading evidence packages...</p>}
        {error && <p className="error-text">{error}</p>}
        {downloadStatus.state === "error" && downloadStatus.message && (
          <p className="error-text">{downloadStatus.message}</p>
        )}
        {!loading && !error && packages.length === 0 && (
          <p>No evidence packages matched your search.</p>
        )}

        {!loading && !error && packages.length > 0 && (
          <>
            <PaginationControls />
            <div className="table-wrap admin-packages-table-wrap">
              <table className="interactive-table admin-packages-table">
                <thead>
                  <tr>
                    <th>Client</th>
                    <th>Package</th>
                    <th>Stage</th>
                    <th>Status</th>
                    <th>CMMC Level</th>
                    <th>Readiness</th>
                    <th>Updated</th>
                    <th>Detail</th>
                    <th>Download</th>
                  </tr>
                </thead>
                <tbody>
                  {packages.map((pkg, index) => {
                    const key = packageKey(pkg, index);
                    const isSelected =
                      selectedPackage &&
                      `${text(selectedPackage, "clientId")}#${packageId(selectedPackage)}` ===
                        `${text(pkg, "clientId")}#${packageId(pkg)}`;
                    const isPreparingDownload =
                      downloadStatus.state === "preparing" &&
                      downloadStatus.packageKey === key;
                    return (
                      <tr
                        key={key}
                        className={isSelected ? "selected-row" : undefined}
                        onClick={() => setSelectedPackage(pkg)}
                      >
                        <td>
                          <strong>
                            {value(text(pkg, "clientName", "clientId"))}
                          </strong>
                          <div className="cell-secondary mono-cell">
                            {value(pkg.clientId)}
                          </div>
                        </td>
                        <td className="package-summary-cell">
                          <strong>{packageDisplayTitle(pkg)}</strong>
                          <div
                            className="cell-secondary mono-cell"
                            title={packageId(pkg)}
                          >
                            {compactPackageIdentifier(packageId(pkg))}
                          </div>
                          {pkg.jobId && (
                            <div className="cell-secondary mono-cell">
                              Job:{" "}
                              {compactPackageIdentifier(String(pkg.jobId), 30)}
                            </div>
                          )}
                          {relatedPackage(pkg) !== "—" && (
                            <div className="cell-secondary">
                              {compactPackageIdentifier(
                                relatedPackage(pkg),
                                46,
                              )}
                            </div>
                          )}
                        </td>
                        <td>
                          <span
                            className={statusPillClassName(
                              rawPackageStage(pkg) || packageStageLabel(pkg),
                            )}
                          >
                            {packageStageLabel(pkg)}
                          </span>
                          <div className="cell-secondary">
                            {packageTypeLabel(pkg)}
                          </div>
                        </td>
                        <td>
                          <span
                            className={statusPillClassName(
                              rawPackageStatus(pkg),
                            )}
                          >
                            {packageStatus(pkg)}
                          </span>
                        </td>
                        <td>{value(text(pkg, "cmmcLevel", "cmmc_level"))}</td>
                        <td>
                          <ReadinessBadges pkg={pkg} compact />
                        </td>
                        <td>{formatLocalDateTime(packageUpdatedAt(pkg))}</td>
                        <td onClick={(event) => event.stopPropagation()}>
                          {text(pkg, "clientId") && text(pkg, "accountId") && packageId(pkg) ? (
                            <Link
                              className="secondary-button table-action-button"
                              to={`/evidence-packages/${encodeURIComponent(text(pkg, "clientId"))}/${encodeURIComponent(text(pkg, "accountId"))}/${encodeURIComponent(packageId(pkg))}`}
                            >
                              Controls
                            </Link>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td onClick={(event) => event.stopPropagation()}>
                          <button
                            className="primary-button table-action-button"
                            type="button"
                            disabled={isPreparingDownload}
                            onClick={() => void handleDownload(pkg)}
                          >
                            {isPreparingDownload ? "Preparing..." : "Download"}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <PaginationControls />
          </>
        )}
      </article>

      {selectedPackage && <PackageDetails pkg={selectedPackage} />}
    </div>
  );
}
